import React, { ReactNode, useState, useEffect, CSSProperties } from 'react'
import Button2 from '@embracesbs/component-button2'
import Icon from '@embracesbs/component-icon'
import { useSubscriptions } from '../Context/SubscriptionContext'
import { useConfig } from '../Config/ConfigContext'
import { useCalmMode } from '../Context/PreferencesContext'

const STORAGE_KEY = 'embrace-ui-details-panel-preference'

const readStoredDetailsPreference = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === null ? true : stored === 'true'
  } catch (error) {
    return true
  }
}

interface PageLayoutProps {
  section1Content: ReactNode
  section2Content: ReactNode
  section3Content: ReactNode
}

const PageLayout: React.FC<PageLayoutProps> = ({ section1Content, section2Content, section3Content }) => {
  const { setSelectedSubscriptionId } = useSubscriptions()
  const config = useConfig()

  const handleLogoClick = () => {
    setSelectedSubscriptionId(null)
  }

  // Get environment display name
  const getEnvironmentName = () => {
    const environments = config?.environments || []

    if (environments.length === 0) {
      return 'Unknown'
    }

    // Format environment names nicely
    const formattedEnvs = environments
      .map((env) => {
        return env
          .replace(/-/g, ' ')
          .split(' ')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      })
      .join(' & ')

    return formattedEnvs
  }

  const environmentName = getEnvironmentName()

  // Calm mode: the details panel starts folded and the subscriptions/tenants
  // list gets a collapse handle, so the tenant form can stand alone.
  const isCalm = useCalmMode()

  const [isDetailsOpen, setIsDetailsOpen] = useState(() => (isCalm ? false : readStoredDetailsPreference()))
  const [isListOpen, setIsListOpen] = useState(true)

  // Calm turning on folds the panel; turning it off restores the stored preference.
  useEffect(() => {
    setIsDetailsOpen(isCalm ? false : readStoredDetailsPreference())
  }, [isCalm])

  useEffect(() => {
    // Calm sessions never persist the panel state: calm always starts folded,
    // and the user's regular preference must survive unchanged.
    if (isCalm) return
    try {
      localStorage.setItem(STORAGE_KEY, isDetailsOpen.toString())
    } catch (error) {
      console.debug('Could not save panel preference to localStorage')
    }
  }, [isDetailsOpen, isCalm])

  // The list collapse only exists in calm mode; outside it the column is
  // exactly the historical layout.
  const listIsVisible = !isCalm || isListOpen
  const listColumn = isCalm ? (isListOpen ? 'min(100%, 27rem)' : '0rem') : 'minmax(min(100%, 27rem), 1fr)'

  const gridContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `${listColumn} ${isDetailsOpen ? '2.5fr 1fr' : '3.5fr 0fr'}`,
    gap: '0px',
    height: 'var(--app-height, 100vh)',
    transition: 'grid-template-columns 0.3s ease',
    position: 'relative' as const,
    overflow: 'hidden'
  }

  const section3WrapperStyle: CSSProperties = {
    position: 'relative' as const,
    height: 'var(--app-height, 100vh)',
    overflow: 'visible'
  }

  const section3ContentWrapperStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    transform: isDetailsOpen ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 0.3s ease',
    backgroundColor: 'var(--tma-page-bg)'
  }

  const section3Style: CSSProperties = {
    // Extra left padding keeps the content clear of the collapse handle on the panel's left edge.
    padding: '0 20px 20px 40px',
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'auto'
  }

  // Outer wrapper stays overflow-visible so the calm-mode collapse handle can
  // hang over the middle column; the inner clip hides the sliding content.
  const section1WrapperStyle: CSSProperties = {
    position: 'relative' as const,
    height: 'var(--app-height, 100vh)',
    overflow: 'visible'
  }

  const section1ClipStyle: CSSProperties = {
    height: '100%',
    overflow: 'hidden'
  }

  const section1Style: CSSProperties = {
    padding: 0,
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    // Keeps the list content at its natural width while the column collapses,
    // so it clips instead of reflowing.
    minWidth: isCalm ? 'min(100vw, 27rem)' : undefined
  }

  const center: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    height: '52px',
    flexShrink: 0,
    boxSizing: 'border-box',
    width: '100%',
    marginBottom: '20px',
    background: 'var(--tma-band-bg)',
    padding: '0 24px'
  }

  const section2Style: CSSProperties = {
    padding: '20px',
    paddingLeft: '40px',
    paddingRight: '40px',
    paddingTop: '0px',
    background: 'var(--tma-panel-bg)',
    height: 'var(--app-height, 100vh)',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  }

  const toggleButtonStyle: CSSProperties = {
    position: 'absolute',
    left: isDetailsOpen ? '0px' : '-30px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'var(--tma-accent)',
    width: '30px',
    height: '48px',
    zIndex: 100,
    cursor: 'pointer',
    borderRadius: isDetailsOpen ? '0 8px 8px 0' : '8px 0 0 8px',
    boxShadow: isDetailsOpen ? '3px 0 8px rgba(0, 0, 0, 0.18)' : '-3px 0 8px rgba(0, 0, 0, 0.18)',
    border: 'none',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'left 0.3s ease, border-radius 0.3s ease'
  }

  // Mirror of the details-panel handle, on the list's right edge (calm mode only).
  const listToggleButtonStyle: CSSProperties = {
    position: 'absolute',
    right: listIsVisible ? '0px' : '-30px',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'var(--tma-accent)',
    width: '30px',
    height: '48px',
    zIndex: 100,
    cursor: 'pointer',
    borderRadius: listIsVisible ? '8px 0 0 8px' : '0 8px 8px 0',
    boxShadow: listIsVisible ? '-3px 0 8px rgba(0, 0, 0, 0.18)' : '3px 0 8px rgba(0, 0, 0, 0.18)',
    border: 'none',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'right 0.3s ease, border-radius 0.3s ease'
  }

  return (
    <div style={gridContainerStyle}>
      <div style={section1WrapperStyle}>
        {isCalm && (
          <Button2
            style={listToggleButtonStyle}
            buttonSize='30px'
            leftElement={<Icon iconName={listIsVisible ? 'ArrowLeft1' : 'ArrowRight1'} size={16} color='#FFFFFF' />}
            onClick={() => setIsListOpen(!isListOpen)}
            variant='subtle'
            aria-label={listIsVisible ? 'Hide subscription list' : 'Show subscription list'}
          />
        )}
        <div style={section1ClipStyle}>
          <div style={section1Style}>
            <div style={{ ...center }}>
              <div onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'var(--tma-band-text)',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    }}
                  >
                    Tenants Management Application
                  </span>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'var(--tma-band-text-muted)',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      cursor: 'default'
                    }}
                  >
                    {environmentName}
                  </span>
                </div>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0 0 20px 20px' }}>
              {section1Content}
            </div>
          </div>
        </div>
      </div>
      <div style={section2Style}>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{section2Content}</div>
      </div>
      <div style={section3WrapperStyle}>
        <Button2
          style={toggleButtonStyle}
          buttonSize='30px'
          leftElement={<Icon iconName={isDetailsOpen ? 'ArrowRight1' : 'ArrowLeft1'} size={16} color='#FFFFFF' />}
          onClick={() => setIsDetailsOpen(!isDetailsOpen)}
          variant='subtle'
          aria-label={isDetailsOpen ? 'Close details panel' : 'Open details panel'}
        />
        <div style={section3ContentWrapperStyle}>
          <div style={section3Style} className='custom-scrollbar'>
            {section3Content}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PageLayout
